import { useEffect, useMemo, useRef, useState } from 'react'
import { loadIgnoredTokenNames } from '@/utils/ignoredTokens'
import { Button } from '@/components/common/Button'
import { CharacterRuler } from './CharacterRuler'
import { loadSplitHints } from '@/utils/loadSplitHints'
import { characterTones, EMPTY_SPLIT_HINTS, unambiguousCuts, withConfirmed, type ConfirmedToken, type SplitHints } from '@/utils/splitSuggestion'
import {
  buildSplitGroups,
  defaultNestedCuts,
  partRanges,
  proposalCuts,
  splitAtCuts,
  withNestedCuts,
  type SplitGroupDraft,
} from '@/utils/tokenSplit'

export interface MultiSplitResult {
  token: string
  parts: string[]
}

interface NestedState {
  partIndex: number
  cuts: number[]
}

interface EditorState {
  token: string
  cuts: number[]
  nested: NestedState | null
  skip: boolean
  touched: boolean
}

export interface MultiSplitApplyOptions {
  close?: boolean
  revoke?: string[]
}

function sameCuts(left: number[], right: number[]): boolean {
  return left.length === right.length && left.every((cut, index) => cut === right[index])
}

function refineGroups(groups: SplitGroupDraft[], hints: SplitHints): SplitGroupDraft[] {
  return groups.map((group) => {
    const repCuts = proposalCuts(group.representative, hints)
    const members: string[] = []
    const moved: string[] = []
    for (const token of group.members) {
      if (sameCuts(proposalCuts(token, hints), repCuts)) members.push(token)
      else moved.push(token)
    }
    return {
      ...group,
      members,
      representative: members[0] || group.representative,
      outliers: [...group.outliers, ...moved],
    }
  })
}

function emptyEditor(token: string, hints: SplitHints): EditorState {
  return { token, cuts: proposalCuts(token, hints), nested: null, skip: false, touched: false }
}

function keepEditor(existing: EditorState | undefined, token: string, hints: SplitHints): EditorState {
  if (existing?.touched && existing.token === token) return existing
  return emptyEditor(token, hints)
}

function partsFor(token: string, editor: EditorState, hints: SplitHints): string[] {
  if (editor.skip) return [token]
  if (editor.nested) return splitAtCuts(token, withNestedCuts(token, editor.cuts, editor.nested))
  if (!editor.touched) return splitAtCuts(token, proposalCuts(token, hints))
  return splitAtCuts(token, editor.cuts)
}

export function MultiSplitDialog({
  isOpen,
  tokens,
  hints: hintsProp,
  confirmed,
  onApply,
  onCancel,
}: {
  isOpen: boolean
  tokens: string[]
  hints?: SplitHints
  confirmed?: ConfirmedToken[]
  onApply: (results: MultiSplitResult[], options?: MultiSplitApplyOptions) => void
  onCancel: () => void
}) {
  const [drafts, setDrafts] = useState<SplitGroupDraft[]>([])
  const [groupEditors, setGroupEditors] = useState<Record<string, EditorState>>({})
  const [outlierEditors, setOutlierEditors] = useState<Record<string, EditorState>>({})
  const [loadedHints, setLoadedHints] = useState<SplitHints>(EMPTY_SPLIT_HINTS)
  const [hintsLoaded, setHintsLoaded] = useState(Boolean(hintsProp))
  const [ignoredLoaded, setIgnoredLoaded] = useState(false)
  const [ignored, setIgnored] = useState<string[]>([])
  const autoSent = useRef(new Set<string>())
  const released = useRef(new Set<string>())
  const [autoTokens, setAutoTokens] = useState<string[]>([])
  const [releasedTokens, setReleasedTokens] = useState<string[]>([])
  const hints = useMemo(
    () =>
      withConfirmed(hintsProp ?? loadedHints, [
        ...(confirmed || []),
        ...ignored.map((text) => ({ text, field: 'ignore' })),
      ]),
    [hintsProp, loadedHints, confirmed, ignored],
  )

  useEffect(() => {
    if (!isOpen) return
    if (hintsProp) {
      setHintsLoaded(true)
      return
    }
    let cancelled = false
    setHintsLoaded(false)
    loadSplitHints()
      .then((next) => {
        if (!cancelled) setLoadedHints(next)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setHintsLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, hintsProp])

  useEffect(() => {
    if (!isOpen) {
      autoSent.current = new Set()
      released.current = new Set()
      setAutoTokens([])
      setReleasedTokens([])
      setIgnoredLoaded(false)
      return
    }
    let cancelled = false
    void loadIgnoredTokenNames()
      .then((names) => {
        if (cancelled) return
        setIgnored((prev) => (prev.join('\n') === names.join('\n') ? prev : names))
      })
      .catch(() => {
        if (cancelled) return
        setIgnored((prev) => (prev.length === 0 ? prev : []))
      })
      .finally(() => {
        if (!cancelled) setIgnoredLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      setDrafts([])
      setGroupEditors({})
      setOutlierEditors({})
      return
    }
    const next = refineGroups(buildSplitGroups(tokens), hints)
    setDrafts(next)
    setGroupEditors((prev) => {
      const groups: Record<string, EditorState> = {}
      for (const group of next) groups[group.id] = keepEditor(prev[group.id], group.representative, hints)
      return groups
    })
    setOutlierEditors((prev) => {
      const outliers: Record<string, EditorState> = {}
      for (const group of next) {
        for (const token of group.outliers) outliers[token] = keepEditor(prev[token], token, hints)
      }
      return outliers
    })
  }, [isOpen, tokens, hints])

  const results = useMemo(() => {
    const out: MultiSplitResult[] = []
    for (const group of drafts) {
      const editor = groupEditors[group.id]
      if (editor && !editor.skip) {
        for (const token of group.members) {
          const parts = partsFor(token, editor, hints)
          if (parts.length >= 2 && !autoSent.current.has(token)) out.push({ token, parts })
        }
      }
      for (const token of group.outliers) {
        const editor = outlierEditors[token]
        if (!editor || editor.skip || autoSent.current.has(token)) continue
        const parts = partsFor(token, editor, hints)
        if (parts.length >= 2) out.push({ token, parts })
      }
    }
    return out
  }, [drafts, groupEditors, outlierEditors, hints])

  useEffect(() => {
    if (!isOpen || !hintsLoaded || !ignoredLoaded) return
    const auto: MultiSplitResult[] = []
    for (const token of tokens) {
      if (autoSent.current.has(token) || released.current.has(token)) continue
      const cuts = unambiguousCuts(token, hints)
      if (!cuts) continue
      const parts = splitAtCuts(token, cuts)
      if (parts.length < 2) continue
      if (parts.join('').length < token.replace(/[\s.\-_/]+/g, '').length) continue
      auto.push({ token, parts })
    }
    if (auto.length === 0) return
    for (const row of auto) autoSent.current.add(row.token)
    setAutoTokens((prev) => [...prev, ...auto.map((row) => row.token)])
    onApply(auto, { close: tokens.length === 1 })
  }, [isOpen, hintsLoaded, ignoredLoaded, tokens, hints, onApply])

  const liveAuto = autoTokens.filter((token) => !releasedTokens.includes(token))

  const releaseAuto = (names: string[]) => {
    const queued = names.filter((token) => autoSent.current.has(token))
    let changed = false
    for (const token of names) {
      autoSent.current.delete(token)
      if (!released.current.has(token)) {
        released.current.add(token)
        changed = true
      }
    }
    if (changed) setReleasedTokens((prev) => [...new Set([...prev, ...names])])
    if (queued.length > 0) onApply([], { close: false, revoke: queued })
  }

  const apply = () => {
    for (const row of results) autoSent.current.add(row.token)
    const revoke = [...released.current]
    const options: MultiSplitApplyOptions = revoke.length > 0 ? { close: true, revoke } : { close: true }
    if (results.length === 0) {
      if (liveAuto.length === 0) return
      onApply([], options)
      return
    }
    onApply(results, options)
  }

  const splitMark = (token: string, touched: boolean) => {
    if (!autoTokens.includes(token)) return ''
    if (touched || releasedTokens.includes(token)) return ' · geändert'
    return ' · automatisch'
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div
        className="flex max-h-[92vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between border-b px-4 py-3">
          <div>
            <h3 className="text-lg font-medium">{tokens.length} Token teilen</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Gleiche Struktur eine Gruppe. Blauer Strich ist der Vorschlag und gilt für alle in der Gruppe.
            </p>
            {hints.failed?.length ? (
              <p className="mt-1 text-xs text-amber-700">{hints.failed.join(', ')} konnten nicht geladen werden.</p>
            ) : null}
          </div>
          <button type="button" className="rounded-lg bg-gray-100 px-2 text-lg leading-none text-gray-500" onClick={onCancel}>
            ×
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-auto px-4 py-3">
          {drafts.map((group) => {
            const editor = groupEditors[group.id] || emptyEditor(group.representative, hints)
            const cuts = withNestedCuts(group.representative, editor.cuts, editor.nested)
            const ranges = partRanges(group.representative, editor.cuts)
            return (
              <div key={group.id} className="overflow-hidden rounded-lg border border-gray-200">
                <div className="border-b bg-gray-50 px-3 py-2">
                  <strong className="block text-sm">{group.label}</strong>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {group.members.length} gleich
                    {group.outliers.length > 0 ? ` · ${group.outliers.length} abweichend` : ''}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {group.members.map((token) => (
                      <code key={token} className="rounded border border-gray-200 bg-white px-1.5 py-0.5 font-mono text-[11px]">
                        {token}
                      </code>
                    ))}
                  </div>
                </div>
                {group.members.length > 0 ? (
                  <div className="px-3 py-3">
                    <CharacterRuler
                      token={group.representative}
                      cuts={cuts}
                      tones={characterTones(group.representative, cuts, hints, ignored)}
                      onChange={(next) => {
                        setGroupEditors((prev) => ({
                          ...prev,
                          [group.id]: {
                            ...(prev[group.id] || editor),
                            token: group.representative,
                            cuts: next,
                            nested: null,
                            touched: true,
                          },
                        }))
                        releaseAuto(group.members)
                      }}
                    />
                    <div className="mt-2 space-y-0.5 text-xs text-gray-600">
                      {group.members.map((token) => (
                        <div key={token}>
                          <code className="font-mono">{token}</code>
                          {' → '}
                          {partsFor(token, editor, hints).join(' | ')}
                          {splitMark(token, editor.touched)}
                        </div>
                      ))}
                    </div>
                    {ranges.length > 1 ? (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {ranges.map((range, index) =>
                          range.text.length >= 2 ? (
                            <button
                              key={`${range.text}-${index}`}
                              type="button"
                              className="rounded border border-dashed border-blue-300 bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700"
                              onClick={() => {
                                setGroupEditors((prev) => ({
                                  ...prev,
                                  [group.id]: {
                                    ...(prev[group.id] || editor),
                                    token: group.representative,
                                    touched: true,
                                    nested:
                                      editor.nested?.partIndex === index
                                        ? null
                                        : { partIndex: index, cuts: defaultNestedCuts(range.text, hints) },
                                  },
                                }))
                                releaseAuto(group.members)
                              }}
                            >
                              {range.text} weiter teilen
                            </button>
                          ) : null,
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {group.outliers.map((token) => {
                  const outlier = outlierEditors[token] || emptyEditor(token, hints)
                  const outlierCuts = withNestedCuts(token, outlier.cuts, outlier.nested)
                  return (
                    <div key={token} className="border-t border-dashed border-orange-200 bg-orange-50/60 px-3 py-3">
                      <div className="mb-2 flex items-center justify-between">
                        <h6 className="text-xs font-semibold text-orange-800">Abweichend · {token}</h6>
                        <label className="text-xs text-gray-500">
                          <input
                            type="checkbox"
                            className="mr-1"
                            checked={outlier.skip}
                            onChange={(event) => {
                              setOutlierEditors((prev) => ({
                                ...prev,
                                [token]: {
                                  ...(prev[token] || outlier),
                                  token,
                                  skip: event.target.checked,
                                  touched: true,
                                },
                              }))
                              if (event.target.checked) releaseAuto([token])
                            }}
                          />
                          überspringen
                        </label>
                      </div>
                      {!outlier.skip ? (
                        <CharacterRuler
                          token={token}
                          cuts={outlierCuts}
                          tones={characterTones(token, outlierCuts, hints, ignored)}
                          onChange={(next) => {
                            setOutlierEditors((prev) => ({
                              ...prev,
                              [token]: {
                                ...(prev[token] || outlier),
                                token,
                                cuts: next,
                                nested: null,
                                touched: true,
                              },
                            }))
                            releaseAuto([token])
                          }}
                        />
                      ) : null}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
        <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
          <span className="mr-auto text-xs text-gray-500">
            {liveAuto.length > 0 ? `${liveAuto.length} automatisch · ` : ''}
            {results.length} bereit · {tokens.length - results.length - liveAuto.length} übersprungen
          </span>
          <Button variant="secondary" onClick={onCancel}>
            Abbrechen
          </Button>
          <Button onClick={apply} disabled={results.length === 0 && liveAuto.length === 0}>
            {liveAuto.length > 0 && results.length === 0
              ? 'Fertig'
              : `${results.length} Splittungen anwenden`}
          </Button>
        </div>
      </div>
    </div>
  )
}
